import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import type {StaffingField, StaffingValues} from 'helpers/staffingValues';
import {t} from 'i18next';
import styles from 'views/workstreams/detail/WorkstreamDetailsShell.module.css';
import {
  ALLOCATION_FIELDS,
  EMPTY_VALUE,
  formatAmount,
  GEO_FIELDS,
  type WorkstreamStaffingSummary,
} from 'views/workstreams/detail/WorkstreamStaffing';

const Value = ({children}: {children: string}) => (
  <Text tagName="dd" size="large" isBold className={styles.staffingStripValue}>
    {children}
  </Text>
);

// A labeled group of stats: each value over its label (the label stays first in
// the DOM, so it reads "Growth 4").
const Group = ({
  label,
  values,
  fields,
}: {
  label: string;
  values: StaffingValues | null;
  fields: Array<[string, StaffingField]>;
}) => (
  <Box display="flex" flexDirection="column" rowGap="4" className={styles.staffingStripGroup}>
    <Text size="small" color="subtle">
      {label}
    </Text>
    <dl className={styles.staffingStripStats}>
      {fields.map(([fieldLabel, key]) => (
        <div key={key} className={styles.staffingStripStat}>
          <Text tagName="dt" size="small" color="subtle">
            {fieldLabel}
          </Text>
          <Value>{values ? formatAmount(values[key]) : EMPTY_VALUE}</Value>
        </div>
      ))}
    </dl>
  </Box>
);

/**
 * /workstreams detail: staffing in the properties line's Staffing popover —
 * headcount, then the allocation and geo splits, side by side. Read-only; values
 * are edited per pillar on the wireframe data page.
 */
export const StaffingStrip = ({staffing}: {staffing: WorkstreamStaffingSummary}) => (
  <section aria-label={t('workstream_staffing')} className={styles.staffingStrip}>
    <dl className={styles.staffingStripGroup}>
      <Box display="flex" flexDirection="column" rowGap="4">
        <Text tagName="dt" size="small" color="subtle">
          {t('workstream_staffing_headcount')}
        </Text>
        <Value>{staffing.headcount}</Value>
      </Box>
    </dl>
    <Group
      label={t('workstream_staffing_allocation')}
      values={staffing.values}
      fields={ALLOCATION_FIELDS()}
    />
    <Group label={t('workstream_staffing_geos')} values={staffing.values} fields={GEO_FIELDS()} />
  </section>
);
